import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { AuthContext } from '../../../components/AuthContext';
import Swal from 'sweetalert2';
import TileSelectionModal from '../../../components/TileSelectionModal';
import { 
  FaCalendarAlt, 
  FaMapMarkerAlt, 
  FaFileExport, 
  FaSearch,
  FaDownload,
  FaArrowRight,
  FaArrowLeft,
  FaBox,
  FaWarehouse,
  FaTruck,
  FaCube,
  FaLayerGroup,
  FaExclamationTriangle,
  FaExpand,
  FaCompress,
  FaBook,
  FaTimes,
  FaTrashAlt
} from 'react-icons/fa';

export default function TilesLedger() {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [ledgerData, setLedgerData] = useState(null);
  const [locations, setLocations] = useState([]);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    location: '',
    tileId: null
  });

  // Selected tile object from modal
  const [selectedTile, setSelectedTile] = useState(null);
  const [showTileModal, setShowTileModal] = useState(false);

  // Fullscreen
  const containerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }, []);
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');

  // Sync dark mode with localStorage changes
  useEffect(() => {
    const syncDarkMode = () => {
      const isDark = localStorage.getItem('darkMode') === 'true';
      setDarkMode(isDark);
    };
    const interval = setInterval(syncDarkMode, 500);
    return () => clearInterval(interval);
  }, []);

  // Handle tile selection from modal
  const handleTileSelect = (tile) => {
    setSelectedTile(tile);
    setFilters(prev => ({
      ...prev,
      tileId: { value: tile._id, label: tile.tilesName || tile.name }
    }));
    setShowTileModal(false);
  };

  const clearTileSelection = () => {
    setSelectedTile(null);
    setFilters(prev => ({ ...prev, tileId: null }));
  };

  // Get company ID from user context
  const getCompanyId = () => {
    return user?.user?.company?._id || user?.user?.company || '';
  };

  useEffect(() => {
    fetchLocations();
    // Set default date range as current month (from 1st day of current month to today)
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 2);

    setFilters(prev => ({
      ...prev,
      dateFrom: startOfMonth.toISOString().split('T')[0],
      dateTo: now.toISOString().split('T')[0]
    }));
  }, []);

  const fetchLocations = async () => {
    try {
      const companyId = getCompanyId();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/locations${companyId ? `/companyId/${companyId}` : ''}`,
        {
          credentials: 'include'
        }
      );
      const data = await response.json();
      const locs = Array.isArray(data) ? data : [];
      setLocations(locs);
    } catch (error) {
      console.error('Error fetching locations:', error);
      Swal.fire('Error', 'Failed to fetch locations', 'error');
    }
  };

  const fetchLedgerData = async () => {
    // Validation
    if (!filters.dateFrom || !filters.dateTo) {
      Swal.fire('Error', 'Please select date range', 'error');
      return;
    }
    
    if (!filters.location) {
      Swal.fire({
        icon: 'error',
        title: 'Location Required',
        text: 'Please select a location to generate the ledger report',
        confirmButtonColor: '#7c3aed'
      });
      return;
    }
    
    if (!filters.tileId) {
      Swal.fire('Error', 'Please select a tile', 'error');
      return;
    }

    setLoading(true);
    try {
      const companyId = getCompanyId();
      const params = new URLSearchParams({
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        tileId: filters.tileId.value,
        location: filters.location,
        ...(companyId && { companyId })
      });

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/reports/tiles/ledger?${params}`,
        {
          credentials: 'include'
        }
      );

      const data = await response.json();
      if (data.success) {
        setLedgerData(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch data');
      }
    } catch (error) {
      console.error('Error fetching ledger data:', error);
      Swal.fire('Error', 'Failed to fetch ledger data', 'error');
      setLedgerData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const exportToCSV = async () => {
    if (!filters.dateFrom || !filters.dateTo || !filters.tileId || !filters.location) {
      Swal.fire('Error', 'Please select tile, location and date range', 'error');
      return;
    }

    setLoading(true);
    try {
      const companyId = getCompanyId();
      const params = new URLSearchParams({
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        tileId: filters.tileId.value,
        location: filters.location,
        ...(companyId && { companyId }),
        format: 'csv'
      });

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/reports/tiles/ledger?${params}`,
        {
          credentials: 'include'
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Tiles_Ledger_${filters.tileId.label}_${filters.dateFrom}_to_${filters.dateTo}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        Swal.fire('Success', 'CSV file downloaded successfully', 'success');
      } else {
        throw new Error('Failed to download CSV file');
      }
    } catch (error) {
      console.error('Error downloading CSV:', error);
      Swal.fire('Error', 'Failed to download CSV file', 'error');
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = async () => {
    if (!filters.dateFrom || !filters.dateTo || !filters.tileId || !filters.location) {
      Swal.fire('Error', 'Please select tile, location and date range', 'error');
      return;
    }

    setLoading(true);
    try {
      const companyId = getCompanyId();
      const params = new URLSearchParams({
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        tileId: filters.tileId.value,
        location: filters.location,
        ...(companyId && { companyId }),
        format: 'excel'
      });

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/reports/tiles/ledger?${params}`,
        {
          credentials: 'include'
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Tiles_Ledger_${filters.tileId.label}_${filters.dateFrom}_to_${filters.dateTo}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        Swal.fire('Success', 'Excel file downloaded successfully', 'success');
      } else {
        throw new Error('Failed to download Excel file');
      }
    } catch (error) {
      console.error('Error downloading Excel:', error);
      Swal.fire('Error', 'Failed to download Excel file', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getTransactionTypeIcon = (type) => {
    switch (type) {
      case 'INWARD':
        return <FaArrowRight className={darkMode ? 'text-green-400' : 'text-green-600'} />;
      case 'OUTWARD':
        return <FaArrowLeft className={darkMode ? 'text-red-400' : 'text-red-600'} />;
      case 'DEMOLISHED':
        return <FaTrashAlt className={darkMode ? 'text-orange-400' : 'text-orange-600'} />;
      case 'TRANSFER_IN':
        return <FaTruck className={darkMode ? 'text-blue-400' : 'text-blue-600'} />;
      case 'TRANSFER_OUT':
        return <FaTruck className={darkMode ? 'text-orange-400' : 'text-orange-600'} />;
      default:
        return <FaBox className={darkMode ? 'text-gray-400' : 'text-gray-600'} />;
    }
  };

  const getTransactionTypeColor = (type) => {
    if (darkMode) {
      switch (type) {
        case 'INWARD':
          return 'bg-green-900/50 text-green-400 border-green-700';
        case 'OUTWARD':
          return 'bg-red-900/50 text-red-400 border-red-700';
        case 'DEMOLISHED':
          return 'bg-orange-900/50 text-orange-400 border-orange-700';
        case 'TRANSFER_IN':
          return 'bg-blue-900/50 text-blue-400 border-blue-700';
        case 'TRANSFER_OUT':
          return 'bg-orange-900/50 text-orange-400 border-orange-700';
        default:
          return 'bg-gray-900/50 text-gray-400 border-gray-700';
      }
    }
    switch (type) {
      case 'INWARD':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'OUTWARD':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'DEMOLISHED':
        return 'bg-orange-50 text-orange-700 border-orange-200';
      case 'TRANSFER_IN':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'TRANSFER_OUT':
        return 'bg-orange-50 text-orange-700 border-orange-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const calculateTotalBoxes = (stock) => {
    if (!stock) return 0;
    return (stock.fsBox || 0) + (stock.dsBox || 0);
  };

  const calculateTotalPieces = (stock) => {
    if (!stock) return 0;
    return (stock.fsPieces || 0) + (stock.dsPieces || 0);
  };

  const calculateStockTotals = (stock) => {
    if (!stock) return { totalBoxes: 0, totalPieces: 0, grandTotal: 0 };
    
    const totalBoxes = (stock.fsBox || 0) + (stock.dsBox || 0);
    const totalPieces = (stock.fsPieces || 0) + (stock.dsPieces || 0);
    const grandTotal = totalBoxes + totalPieces;
    
    return { totalBoxes, totalPieces, grandTotal };
  };

  return (
    <div ref={containerRef} className={`min-h-screen p-6 transition-colors duration-300 ${darkMode ? 'bg-gradient-to-br from-gray-900 to-gray-800' : 'bg-gradient-to-br from-gray-50 to-gray-100'} ${isFullscreen ? 'overflow-auto' : ''}`}>
      <div className="w-full mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-2xl shadow-2xl p-8">
            <div className="flex items-center gap-4">
              <div className="bg-white/20 p-4 rounded-xl backdrop-blur-sm">
                <FaBook className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-white mb-2">Tiles Ledger</h1>
                <p className="text-white/90">Track tile movement history with stock details</p>
              </div>
              <button onClick={toggleFullscreen} className="p-3 rounded-xl transition-colors hover:bg-white/20 text-white/80 hover:text-white" title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
                {isFullscreen ? <FaCompress className="w-5 h-5" /> : <FaExpand className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Filters - attached below header */}
          <div className={`rounded-b-2xl shadow-xl border-2 border-t-0 p-6 transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {/* Tile - button+modal */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FaCube className="inline mr-2" />
                  Tile *
                </label>
                {selectedTile ? (
                  <div className={`flex items-center justify-between px-3 py-2 border-2 rounded-xl transition-all ${darkMode ? 'bg-gray-700 border-indigo-500/50' : 'bg-indigo-50 border-indigo-300'}`}>
                    <div className="flex-1 min-w-0">
                      <div className={`font-medium text-sm truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedTile.tilesName || selectedTile.name}</div>
                      {selectedTile.id && <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{selectedTile.id}</div>}
                    </div>
                    <button onClick={clearTileSelection} className={`ml-2 p-1 rounded-lg transition-colors ${darkMode ? 'text-gray-400 hover:text-red-400 hover:bg-gray-600' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'}`}>
                      <FaTimes className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowTileModal(true)}
                    className={`w-full px-3 py-2.5 border-2 rounded-xl text-left text-sm transition-all hover:border-indigo-400 ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-400 hover:bg-gray-600' : 'border-gray-200 text-gray-500 hover:bg-indigo-50'}`}
                  >
                    Click to select tile...
                  </button>
                )}
              </div>

              {/* Date From */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FaCalendarAlt className="inline mr-2" />
                  Date From *
                </label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  className={`w-full px-3 py-2.5 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-200 hover:border-gray-300'}`}
                />
              </div>

              {/* Date To */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FaCalendarAlt className="inline mr-2" />
                  Date To *
                </label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  className={`w-full px-3 py-2.5 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-200 hover:border-gray-300'}`}
                />
              </div>

              {/* Location - native select */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FaMapMarkerAlt className="inline mr-2" />
                  Location *
                </label>
                <select
                  value={filters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                  className={`w-full px-3 py-2.5 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all appearance-none cursor-pointer ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-200 hover:border-gray-300 bg-white'}`}
                >
                  <option value="">Select Location</option>
                  {locations.map(loc => (
                    <option key={loc._id} value={loc._id}>{loc.name || loc.locationName}</option>
                  ))}
                </select>
              </div>

              {/* Generate button */}
              <div className="flex items-end">
                <button
                  onClick={fetchLedgerData}
                  disabled={loading}
                  className="w-full px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 flex items-center justify-center gap-2 transition-all shadow-lg hover:shadow-xl font-medium"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Loading...
                    </>
                  ) : (
                    <>
                      <FaSearch />
                      Generate
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Export Options - conditional */}
            {ledgerData && (
              <div className={`mt-4 pt-4 border-t-2 ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                <div className="flex items-center gap-2">
                  <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Export Options:</span>
                  <button
                    onClick={exportToCSV}
                    disabled={loading}
                    className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-1.5 rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all disabled:bg-gray-400 flex items-center gap-2 text-sm shadow-md hover:shadow-lg font-medium"
                  >
                    <FaDownload />
                    CSV
                  </button>
                  <button
                    onClick={exportToExcel}
                    disabled={loading}
                    className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-4 py-1.5 rounded-xl hover:from-blue-600 hover:to-indigo-700 transition-all disabled:bg-gray-400 flex items-center gap-2 text-sm shadow-md hover:shadow-lg font-medium"
                  >
                    <FaFileExport />
                    Excel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Results */}
        {ledgerData ? (
          <div className="space-y-6">
            {/* Tile Information Header */}
            <div className={`rounded-2xl shadow-xl border-2 p-5 transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-2">
                <div>
                  <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {ledgerData.tile.tilesName} <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>({ledgerData.tile.id})</span>
                  </h3>
                  <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {ledgerData.tile.category} | {ledgerData.tile.sizeMM} MM | {ledgerData.tile.brand}
                  </div>
                </div>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Period: {formatDate(filters.dateFrom)} to {formatDate(filters.dateTo)}
                </div>
              </div>

              {/* Stock Information - Compact Grid */}
              <div className="grid grid-cols-3 gap-3 text-xs">
                {/* Opening Stock */}
                <div className={`rounded-xl p-3 border ${darkMode ? 'bg-green-900/30 border-green-700' : 'bg-green-50 border-green-200'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-semibold flex items-center ${darkMode ? 'text-green-400' : 'text-green-900'}`}>
                      <FaBox className="mr-1" size={10} />
                      Opening Stock
                    </h4>
                    <div className={`text-xs ${darkMode ? 'text-green-400/70' : 'text-green-700'}`}>{formatDate(filters.dateFrom)}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <div>
                      <div className={`font-medium ${darkMode ? 'text-green-400/80' : 'text-green-700'}`}>FS Box</div>
                      <div className={darkMode ? 'text-green-300' : 'text-green-900'}>{ledgerData.openingStock?.fsBox || 0}</div>
                    </div>
                    <div>
                      <div className={`font-medium ${darkMode ? 'text-green-400/80' : 'text-green-700'}`}>FS Pcs</div>
                      <div className={darkMode ? 'text-green-300' : 'text-green-900'}>{ledgerData.openingStock?.fsPieces || 0}</div>
                    </div>
                    <div>
                      <div className={`font-medium ${darkMode ? 'text-green-400/80' : 'text-green-700'}`}>CD Box</div>
                      <div className={darkMode ? 'text-green-300' : 'text-green-900'}>{ledgerData.openingStock?.cdBox || 0}</div>
                    </div>
                    <div>
                      <div className={`font-medium ${darkMode ? 'text-green-400/80' : 'text-green-700'}`}>CD Pcs</div>
                      <div className={darkMode ? 'text-green-300' : 'text-green-900'}>{ledgerData.openingStock?.cdPieces || 0}</div>
                    </div>
                    <div>
                      <div className={`font-medium ${darkMode ? 'text-green-400/80' : 'text-green-700'}`}>FD Box</div>
                      <div className={darkMode ? 'text-green-300' : 'text-green-900'}>{ledgerData.openingStock?.fdBox || 0}</div>
                    </div>
                    <div>
                      <div className={`font-medium ${darkMode ? 'text-green-400/80' : 'text-green-700'}`}>FD Pcs</div>
                      <div className={darkMode ? 'text-green-300' : 'text-green-900'}>{ledgerData.openingStock?.fdPieces || 0}</div>
                    </div>
                  </div>
                </div>
                
                {/* Current Stock */}
                <div className={`rounded-xl p-3 border ${darkMode ? 'bg-purple-900/30 border-purple-700' : 'bg-purple-50 border-purple-200'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-semibold flex items-center ${darkMode ? 'text-purple-400' : 'text-purple-900'}`}>
                      <FaWarehouse className="mr-1" size={10} />
                      Current Stock
                    </h4>
                    <div className={`text-xs ${darkMode ? 'text-purple-400/70' : 'text-purple-700'}`}>System Live</div>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <div>
                      <div className={`font-medium ${darkMode ? 'text-purple-400/80' : 'text-purple-700'}`}>FS Box</div>
                      <div className={darkMode ? 'text-purple-300' : 'text-purple-900'}>{ledgerData.currentStock?.fsBox || 0}</div>
                    </div>
                    <div>
                      <div className={`font-medium ${darkMode ? 'text-purple-400/80' : 'text-purple-700'}`}>FS Pcs</div>
                      <div className={darkMode ? 'text-purple-300' : 'text-purple-900'}>{ledgerData.currentStock?.fsPieces || 0}</div>
                    </div>
                    <div>
                      <div className={`font-medium ${darkMode ? 'text-purple-400/80' : 'text-purple-700'}`}>CD Box</div>
                      <div className={darkMode ? 'text-purple-300' : 'text-purple-900'}>{ledgerData.currentStock?.cdBox || 0}</div>
                    </div>
                    <div>
                      <div className={`font-medium ${darkMode ? 'text-purple-400/80' : 'text-purple-700'}`}>CD Pcs</div>
                      <div className={darkMode ? 'text-purple-300' : 'text-purple-900'}>{ledgerData.currentStock?.cdPieces || 0}</div>
                    </div>
                    <div>
                      <div className={`font-medium ${darkMode ? 'text-purple-400/80' : 'text-purple-700'}`}>FD Box</div>
                      <div className={darkMode ? 'text-purple-300' : 'text-purple-900'}>{ledgerData.currentStock?.fdBox || 0}</div>
                    </div>
                    <div>
                      <div className={`font-medium ${darkMode ? 'text-purple-400/80' : 'text-purple-700'}`}>FD Pcs</div>
                      <div className={darkMode ? 'text-purple-300' : 'text-purple-900'}>{ledgerData.currentStock?.fdPieces || 0}</div>
                    </div>
                  </div>
                </div>

                {/* Closing Stock */}
                <div className={`rounded-xl p-3 border ${darkMode ? 'bg-blue-900/30 border-blue-700' : 'bg-blue-50 border-blue-200'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-semibold flex items-center ${darkMode ? 'text-blue-400' : 'text-blue-900'}`}>
                      <FaLayerGroup className="mr-1" size={10} />
                      Closing Stock
                    </h4>
                    <div className={`text-xs ${darkMode ? 'text-blue-400/70' : 'text-blue-700'}`}>{formatDate(filters.dateTo)}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <div>
                      <div className={`font-medium ${darkMode ? 'text-blue-400/80' : 'text-blue-700'}`}>FS Box</div>
                      <div className={darkMode ? 'text-blue-300' : 'text-blue-900'}>{ledgerData.closingStock?.fsBox || 0}</div>
                    </div>
                    <div>
                      <div className={`font-medium ${darkMode ? 'text-blue-400/80' : 'text-blue-700'}`}>FS Pcs</div>
                      <div className={darkMode ? 'text-blue-300' : 'text-blue-900'}>{ledgerData.closingStock?.fsPieces || 0}</div>
                    </div>
                    <div>
                      <div className={`font-medium ${darkMode ? 'text-blue-400/80' : 'text-blue-700'}`}>CD Box</div>
                      <div className={darkMode ? 'text-blue-300' : 'text-blue-900'}>{ledgerData.closingStock?.cdBox || 0}</div>
                    </div>
                    <div>
                      <div className={`font-medium ${darkMode ? 'text-blue-400/80' : 'text-blue-700'}`}>CD Pcs</div>
                      <div className={darkMode ? 'text-blue-300' : 'text-blue-900'}>{ledgerData.closingStock?.cdPieces || 0}</div>
                    </div>
                    <div>
                      <div className={`font-medium ${darkMode ? 'text-blue-400/80' : 'text-blue-700'}`}>FD Box</div>
                      <div className={darkMode ? 'text-blue-300' : 'text-blue-900'}>{ledgerData.closingStock?.fdBox || 0}</div>
                    </div>
                    <div>
                      <div className={`font-medium ${darkMode ? 'text-blue-400/80' : 'text-blue-700'}`}>FD Pcs</div>
                      <div className={darkMode ? 'text-blue-300' : 'text-blue-900'}>{ledgerData.closingStock?.fdPieces || 0}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Transactions Table */}
            <div className={`rounded-2xl shadow-xl border-2 transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className={`px-6 py-4 border-b-2 flex justify-between items-center ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Transaction Details ({ledgerData.transactions.length})
                </h3>
                <div className="flex items-center gap-3 text-xs">
                  <div className="flex items-center gap-1">
                    <span className={`p-1 rounded ${darkMode ? 'bg-green-900/50' : 'bg-green-100'}`}><FaArrowRight className={darkMode ? 'text-green-400' : 'text-green-600'} size={10} /></span>
                    <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Inward</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className={`p-1 rounded ${darkMode ? 'bg-red-900/50' : 'bg-red-100'}`}><FaArrowLeft className={darkMode ? 'text-red-400' : 'text-red-600'} size={10} /></span>
                    <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Outward</span>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto rounded-b-2xl">
                <table className={`min-w-full text-xs border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                  <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                    <tr>
                      <th className={`px-3 py-2 text-left font-medium text-xs uppercase tracking-wider border-b ${darkMode ? 'text-gray-300 border-gray-600' : 'text-gray-500 border-gray-200'}`}>Date/Type</th>
                      <th className={`px-3 py-2 text-left font-medium text-xs uppercase tracking-wider border-b ${darkMode ? 'text-gray-300 border-gray-600' : 'text-gray-500 border-gray-200'}`}>Supplier</th>
                      <th className={`px-3 py-2 text-left font-medium text-xs uppercase tracking-wider border-b ${darkMode ? 'text-gray-300 border-gray-600' : 'text-gray-500 border-gray-200'}`}>Remarks</th>
                      <th className={`px-3 py-2 text-center font-medium text-xs uppercase tracking-wider border-b ${darkMode ? 'bg-green-900/30 text-gray-300 border-gray-600' : 'bg-green-50 text-gray-500 border-gray-200'}`} colSpan="6">Inward</th>
                      <th className={`px-3 py-2 text-center font-medium text-xs uppercase tracking-wider border-b ${darkMode ? 'bg-red-900/30 text-gray-300 border-gray-600' : 'bg-red-50 text-gray-500 border-gray-200'}`} colSpan="6">Outward</th>
                    </tr>
                    <tr className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                      <th className={`px-3 py-1 border-b ${darkMode ? 'border-gray-600' : 'border-gray-200'}`} colSpan="3"></th>
                      <th className={`px-3 py-1 text-center text-xs border-b ${darkMode ? 'bg-green-900/30 text-gray-400 border-gray-600' : 'bg-green-50 text-gray-500 border-gray-200'}`}>FS Box</th>
                      <th className={`px-3 py-1 text-center text-xs border-b ${darkMode ? 'bg-green-900/30 text-gray-400 border-gray-600' : 'bg-green-50 text-gray-500 border-gray-200'}`}>FS Pcs</th>
                      <th className={`px-3 py-1 text-center text-xs border-b ${darkMode ? 'bg-green-900/30 text-gray-400 border-gray-600' : 'bg-green-50 text-gray-500 border-gray-200'}`}>CD Box</th>
                      <th className={`px-3 py-1 text-center text-xs border-b ${darkMode ? 'bg-green-900/30 text-gray-400 border-gray-600' : 'bg-green-50 text-gray-500 border-gray-200'}`}>CD Pcs</th>
                      <th className={`px-3 py-1 text-center text-xs border-b ${darkMode ? 'bg-green-900/30 text-gray-400 border-gray-600' : 'bg-green-50 text-gray-500 border-gray-200'}`}>FD Box</th>
                      <th className={`px-3 py-1 text-center text-xs border-b ${darkMode ? 'bg-green-900/30 text-gray-400 border-gray-600' : 'bg-green-50 text-gray-500 border-gray-200'}`}>FD Pcs</th>
                      <th className={`px-3 py-1 text-center text-xs border-b ${darkMode ? 'bg-red-900/30 text-gray-400 border-gray-600' : 'bg-red-50 text-gray-500 border-gray-200'}`}>FS Box</th>
                      <th className={`px-3 py-1 text-center text-xs border-b ${darkMode ? 'bg-red-900/30 text-gray-400 border-gray-600' : 'bg-red-50 text-gray-500 border-gray-200'}`}>FS Pcs</th>
                      <th className={`px-3 py-1 text-center text-xs border-b ${darkMode ? 'bg-red-900/30 text-gray-400 border-gray-600' : 'bg-red-50 text-gray-500 border-gray-200'}`}>CD Box</th>
                      <th className={`px-3 py-1 text-center text-xs border-b ${darkMode ? 'bg-red-900/30 text-gray-400 border-gray-600' : 'bg-red-50 text-gray-500 border-gray-200'}`}>CD Pcs</th>
                      <th className={`px-3 py-1 text-center text-xs border-b ${darkMode ? 'bg-red-900/30 text-gray-400 border-gray-600' : 'bg-red-50 text-gray-500 border-gray-200'}`}>FD Box</th>
                      <th className={`px-3 py-1 text-center text-xs border-b ${darkMode ? 'bg-red-900/30 text-gray-400 border-gray-600' : 'bg-red-50 text-gray-500 border-gray-200'}`}>FD Pcs</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${darkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'}`}>
                    {ledgerData.transactions.map((transaction, index) => (
                      <tr key={index} className={`${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} transition-colors`}>
                        <td className={`px-3 py-2 ${darkMode ? 'text-white' : ''}`}>
                          <div className="flex items-center gap-1">
                            {getTransactionTypeIcon(transaction.type)}
                            <div>
                              <div className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{formatDate(transaction.date)}</div>
                              <div className={`text-xs px-1 py-0.5 rounded border inline-block ${getTransactionTypeColor(transaction.type)}`}>
                                {transaction.type.replace('_', ' ')}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className={`px-3 py-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {transaction.supplier || 'N/A'}
                        </td>
                        <td className={`px-3 py-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          <div className="max-w-xs truncate" title={transaction.remarks}>
                            {transaction.remarks || 'N/A'}
                          </div>
                        </td>
                        <td className={`px-3 py-2 text-right font-medium ${darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-50 text-green-700'}`}>
                          {transaction.inward?.fsBox || 0}
                        </td>
                        <td className={`px-3 py-2 text-right font-medium ${darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-50 text-green-700'}`}>
                          {transaction.inward?.fsPieces || 0}
                        </td>
                        <td className={`px-3 py-2 text-right font-medium ${darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-50 text-green-700'}`}>
                          {transaction.inward?.cdBox || 0}
                        </td>
                        <td className={`px-3 py-2 text-right font-medium ${darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-50 text-green-700'}`}>
                          {transaction.inward?.cdPieces || 0}
                        </td>
                        <td className={`px-3 py-2 text-right font-medium ${darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-50 text-green-700'}`}>
                          {transaction.inward?.fdBox || 0}
                        </td>
                        <td className={`px-3 py-2 text-right font-medium ${darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-50 text-green-700'}`}>
                          {transaction.inward?.fdPieces || 0}
                        </td>
                        <td className={`px-3 py-2 text-right font-medium ${darkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-50 text-red-700'}`}>
                          {transaction.outward?.fsBox || 0}
                        </td>
                        <td className={`px-3 py-2 text-right font-medium ${darkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-50 text-red-700'}`}>
                          {transaction.outward?.fsPieces || 0}
                        </td>
                        <td className={`px-3 py-2 text-right font-medium ${darkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-50 text-red-700'}`}>
                          {transaction.outward?.cdBox || 0}
                        </td>
                        <td className={`px-3 py-2 text-right font-medium ${darkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-50 text-red-700'}`}>
                          {transaction.outward?.cdPieces || 0}
                        </td>
                        <td className={`px-3 py-2 text-right font-medium ${darkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-50 text-red-700'}`}>
                          {transaction.outward?.fdBox || 0}
                        </td>
                        <td className={`px-3 py-2 text-right font-medium ${darkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-50 text-red-700'}`}>
                          {transaction.outward?.fdPieces || 0}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Order / Transit Summary */}
            {ledgerData?.orderSummary && (
              <div className={`mt-4 rounded-xl border p-4 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <h3 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Order / Transit Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className={`rounded-lg p-3 ${darkMode ? 'bg-purple-900/20 border border-purple-800' : 'bg-purple-50 border border-purple-200'}`}>
                    <div className={`text-xs font-medium mb-2 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>Purchase Orders</div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between"><span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Ordered (Period)</span><span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ledgerData.orderSummary.dateRange?.ordered || 0} boxes</span></div>
                      <div className="flex justify-between"><span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Pending Orders</span><span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ledgerData.orderSummary.current?.pendingOrders || 0} boxes</span></div>
                    </div>
                  </div>
                  <div className={`rounded-lg p-3 ${darkMode ? 'bg-yellow-900/20 border border-yellow-800' : 'bg-yellow-50 border border-yellow-200'}`}>
                    <div className={`text-xs font-medium mb-2 ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>In Transit</div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between"><span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Dispatched (Period)</span><span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ledgerData.orderSummary.dateRange?.dispatched || 0} boxes</span></div>
                      <div className="flex justify-between"><span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Currently In Transit</span><span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ledgerData.orderSummary.current?.inTransit || 0} boxes</span></div>
                    </div>
                  </div>
                  <div className={`rounded-lg p-3 ${darkMode ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-200'}`}>
                    <div className={`text-xs font-medium mb-2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>Sales Orders</div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between"><span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Booked (Period)</span><span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{(ledgerData.orderSummary.dateRange?.salesOrder?.freshBoxes || 0) + (ledgerData.orderSummary.dateRange?.salesOrder?.damagedBoxes || 0)} boxes</span></div>
                      <div className="flex justify-between"><span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Pending Fulfillment</span><span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{(ledgerData.orderSummary.current?.pendingSalesOrders?.freshBoxes || 0) + (ledgerData.orderSummary.current?.pendingSalesOrders?.damagedBoxes || 0)} boxes</span></div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Sales Pipeline Details Table */}
            {ledgerData?.pipelineDetails && (
              (ledgerData.pipelineDetails.salesOrders?.length > 0 || ledgerData.pipelineDetails.tilesOrders?.length > 0 || ledgerData.pipelineDetails.dispatches?.length > 0 || ledgerData.pipelineDetails.deliveryOrders?.length > 0) && (
              <div className={`mt-4 rounded-2xl shadow-xl border-2 transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <div className={`px-6 py-4 border-b-2 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Sales Pipeline Details
                  </h3>
                </div>
                <div className="overflow-x-auto rounded-b-2xl">
                  <table className={`min-w-full text-xs border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                      <tr>
                        <th className={`px-3 py-2 text-left font-medium text-xs uppercase tracking-wider border-b ${darkMode ? 'text-gray-300 border-gray-600' : 'text-gray-500 border-gray-200'}`}>Type</th>
                        <th className={`px-3 py-2 text-left font-medium text-xs uppercase tracking-wider border-b ${darkMode ? 'text-gray-300 border-gray-600' : 'text-gray-500 border-gray-200'}`}>Number</th>
                        <th className={`px-3 py-2 text-left font-medium text-xs uppercase tracking-wider border-b ${darkMode ? 'text-gray-300 border-gray-600' : 'text-gray-500 border-gray-200'}`}>Date</th>
                        <th className={`px-3 py-2 text-left font-medium text-xs uppercase tracking-wider border-b ${darkMode ? 'text-gray-300 border-gray-600' : 'text-gray-500 border-gray-200'}`}>Party</th>
                        <th className={`px-3 py-2 text-center font-medium text-xs uppercase tracking-wider border-b ${darkMode ? 'text-gray-300 border-gray-600' : 'text-gray-500 border-gray-200'}`}>Booked/Ordered</th>
                        <th className={`px-3 py-2 text-center font-medium text-xs uppercase tracking-wider border-b ${darkMode ? 'text-gray-300 border-gray-600' : 'text-gray-500 border-gray-200'}`}>Delivered/Dispatched</th>
                        <th className={`px-3 py-2 text-center font-medium text-xs uppercase tracking-wider border-b ${darkMode ? 'text-gray-300 border-gray-600' : 'text-gray-500 border-gray-200'}`}>Status</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${darkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'}`}>
                      {/* Sales Orders Section */}
                      {ledgerData.pipelineDetails.salesOrders?.length > 0 && (
                        <>
                          <tr className={darkMode ? 'bg-blue-900/20' : 'bg-blue-50'}>
                            <td colSpan="7" className={`px-3 py-1.5 text-xs font-bold uppercase ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                              Sales Order Booking ({ledgerData.pipelineDetails.salesOrders.length})
                            </td>
                          </tr>
                          {ledgerData.pipelineDetails.salesOrders.map((so, idx) => (
                            <tr key={`so-${idx}`} className={`${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} transition-colors`}>
                              <td className={`px-3 py-2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${darkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>SO</span>
                              </td>
                              <td className={`px-3 py-2 font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{so.orderNumber || '—'}</td>
                              <td className={`px-3 py-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{formatDate(so.date)}</td>
                              <td className={`px-3 py-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{so.customer}</td>
                              <td className={`px-3 py-2 text-center font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                {so.freshBoxes} B {so.freshPieces > 0 ? `/ ${so.freshPieces} P` : ''}
                                {so.damagedBoxes > 0 && <span className={`block text-xs ${darkMode ? 'text-red-400' : 'text-red-500'}`}>+{so.damagedBoxes} CD</span>}
                              </td>
                              <td className={`px-3 py-2 text-center font-medium ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                                {so.deliveredFreshBoxes || 0} B
                                {so.deliveredDamagedBoxes > 0 && <span className="block text-xs">+{so.deliveredDamagedBoxes} CD</span>}
                              </td>
                              <td className="px-3 py-2 text-center">
                                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                  so.status === 'Pending' ? (darkMode ? 'bg-yellow-900/40 text-yellow-300' : 'bg-yellow-100 text-yellow-700') :
                                  so.status === 'Completed' ? (darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700') :
                                  (darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700')
                                }`}>{so.status}</span>
                              </td>
                            </tr>
                          ))}
                        </>
                      )}

                      {/* Tiles Orders Section */}
                      {ledgerData.pipelineDetails.tilesOrders?.length > 0 && (
                        <>
                          <tr className={darkMode ? 'bg-purple-900/20' : 'bg-purple-50'}>
                            <td colSpan="7" className={`px-3 py-1.5 text-xs font-bold uppercase ${darkMode ? 'text-purple-400' : 'text-purple-700'}`}>
                              Tiles Order ({ledgerData.pipelineDetails.tilesOrders.length})
                            </td>
                          </tr>
                          {ledgerData.pipelineDetails.tilesOrders.map((order, idx) => (
                            <tr key={`to-${idx}`} className={`${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} transition-colors`}>
                              <td className={`px-3 py-2 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${darkMode ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>Order</span>
                              </td>
                              <td className={`px-3 py-2 font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{order.orderId || '—'}</td>
                              <td className={`px-3 py-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{formatDate(order.date)}</td>
                              <td className={`px-3 py-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{order.supplier}</td>
                              <td className={`px-3 py-2 text-center font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{order.orderedBoxes} B</td>
                              <td className={`px-3 py-2 text-center font-medium ${darkMode ? 'text-green-400' : 'text-green-600'}`}>{order.dispatchedBoxes} B</td>
                              <td className="px-3 py-2 text-center">
                                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                  order.status === 'pending' ? (darkMode ? 'bg-yellow-900/40 text-yellow-300' : 'bg-yellow-100 text-yellow-700') :
                                  order.status === 'completed' ? (darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700') :
                                  order.status === 'partially-dispatched' ? (darkMode ? 'bg-orange-900/40 text-orange-300' : 'bg-orange-100 text-orange-700') :
                                  (darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700')
                                }`}>{order.status}</span>
                              </td>
                            </tr>
                          ))}
                        </>
                      )}

                      {/* Dispatches Section */}
                      {ledgerData.pipelineDetails.dispatches?.length > 0 && (
                        <>
                          <tr className={darkMode ? 'bg-yellow-900/20' : 'bg-yellow-50'}>
                            <td colSpan="7" className={`px-3 py-1.5 text-xs font-bold uppercase ${darkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
                              Dispatched ({ledgerData.pipelineDetails.dispatches.length})
                            </td>
                          </tr>
                          {ledgerData.pipelineDetails.dispatches.map((dispatch, idx) => (
                            <tr key={`d-${idx}`} className={`${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} transition-colors`}>
                              <td className={`px-3 py-2 ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${darkMode ? 'bg-yellow-900/40 text-yellow-300' : 'bg-yellow-100 text-yellow-700'}`}>Dispatch</span>
                              </td>
                              <td className={`px-3 py-2 font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{dispatch.dispatchId}</td>
                              <td className={`px-3 py-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{formatDate(dispatch.date)}</td>
                              <td className={`px-3 py-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>—</td>
                              <td className={`px-3 py-2 text-center font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{dispatch.dispatchedBoxes} B</td>
                              <td className={`px-3 py-2 text-center font-medium ${darkMode ? 'text-green-400' : 'text-green-600'}`}>{dispatch.receivedBoxes} B</td>
                              <td className="px-3 py-2 text-center">
                                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                  dispatch.status === 'in-transit' ? (darkMode ? 'bg-yellow-900/40 text-yellow-300' : 'bg-yellow-100 text-yellow-700') :
                                  dispatch.status === 'received' || dispatch.status === 'completed' ? (darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700') :
                                  (darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700')
                                }`}>{dispatch.status}</span>
                              </td>
                            </tr>
                          ))}
                        </>
                      )}

                      {/* Delivery Orders against SO Section */}
                      {ledgerData.pipelineDetails.deliveryOrders?.length > 0 && (
                        <>
                          <tr className={darkMode ? 'bg-green-900/20' : 'bg-green-50'}>
                            <td colSpan="7" className={`px-3 py-1.5 text-xs font-bold uppercase ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
                              Delivery Order against SO ({ledgerData.pipelineDetails.deliveryOrders.length})
                            </td>
                          </tr>
                          {ledgerData.pipelineDetails.deliveryOrders.map((dOrder, idx) => (
                            <tr key={`do-${idx}`} className={`${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} transition-colors`}>
                              <td className={`px-3 py-2 ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700'}`}>DO</span>
                              </td>
                              <td className={`px-3 py-2 font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                {dOrder.doNumber}
                                {dOrder.salesOrderNumber !== '—' && (
                                  <span className={`block text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>SO: {dOrder.salesOrderNumber}</span>
                                )}
                              </td>
                              <td className={`px-3 py-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{formatDate(dOrder.date)}</td>
                              <td className={`px-3 py-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                {dOrder.recipient}
                                <span className={`block text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{dOrder.recipientType}</span>
                              </td>
                              <td className={`px-3 py-2 text-center font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                {dOrder.freshBoxes} B {dOrder.freshPieces > 0 ? `/ ${dOrder.freshPieces} P` : ''}
                              </td>
                              <td className={`px-3 py-2 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>—</td>
                              <td className="px-3 py-2 text-center">
                                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                  dOrder.status === 'Pending' ? (darkMode ? 'bg-yellow-900/40 text-yellow-300' : 'bg-yellow-100 text-yellow-700') :
                                  dOrder.status === 'Completed' ? (darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700') :
                                  dOrder.status === 'Cancelled' ? (darkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-700') :
                                  (darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700')
                                }`}>{dOrder.status}</span>
                              </td>
                            </tr>
                          ))}
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              )
            )}

          </div>
        ) : (
          <div className={`rounded-2xl shadow-xl border-2 transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className={`px-6 py-12 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {loading ? (
                <div className="flex justify-center items-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
              ) : (
                <div>
                  <FaLayerGroup className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                  <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>No Data Found</h3>
                  <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
                    {filters.dateFrom && filters.dateTo && filters.tileId
                      ? 'No transactions found for the selected criteria.'
                      : 'Select a tile, location and date range, then click Generate to view the ledger report.'
                    }
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Tile Selection Modal */}
      <TileSelectionModal
        isOpen={showTileModal}
        onClose={() => setShowTileModal(false)}
        onSelect={handleTileSelect}
        darkMode={darkMode}
        userId={user?.user?._id}
      />
    </div>
  );
}
